type AvatarSize = 'sm' | 'md' | 'lg';

interface AvatarProps {
  src?: string;
  name: string;
  size?: AvatarSize;
  isAI?: boolean;
}

const sizeStyles: Record<AvatarSize, { container: string; text: string }> = {
  sm: { container: 'w-8 h-8', text: 'text-xs' },
  md: { container: 'w-10 h-10', text: 'text-sm' },
  lg: { container: 'w-12 h-12', text: 'text-base' },
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '??';
  }
  const firstPart = parts[0] ?? '';
  if (parts.length === 1 || firstPart.length === 0) {
    return firstPart.substring(0, 2).toUpperCase() || '??';
  }
  const lastPart = parts[parts.length - 1] ?? '';
  const firstChar = firstPart[0] ?? '?';
  const lastChar = lastPart[0] ?? '?';
  return (firstChar + lastChar).toUpperCase();
}

function getBackgroundColor(name: string): string {
  // Generate consistent color based on name
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  const colors = [
    'bg-blue-600',
    'bg-emerald-600',
    'bg-violet-600',
    'bg-orange-600',
    'bg-cyan-600',
    'bg-rose-600',
    'bg-indigo-600',
    'bg-teal-600',
  ];

  const index = Math.abs(hash) % colors.length;
  return colors[index] ?? 'bg-blue-600';
}

export function Avatar({ src, name, size = 'md', isAI = false }: AvatarProps): JSX.Element {
  const { container, text } = sizeStyles[size];

  const baseStyles = [
    container,
    'rounded-full',
    'flex items-center justify-center',
    'overflow-hidden',
    'flex-shrink-0',
  ].join(' ');

  const aiGlowStyles = isAI
    ? 'border border-accent-ai/20 shadow-sm'
    : 'border border-border-subtle';

  if (src) {
    return (
      <div className={`${baseStyles} ${aiGlowStyles}`}>
        <img
          src={src}
          alt={name}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  const bgColor = isAI ? 'bg-accent-ai/10' : getBackgroundColor(name);
  const textColor = isAI ? 'text-accent-ai' : 'text-text-inverse';

  return (
    <div className={`${baseStyles} ${bgColor} ${aiGlowStyles}`}>
      <span className={`${text} font-medium ${textColor}`}>
        {getInitials(name)}
      </span>
    </div>
  );
}
